/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";
import { Check } from "lucide-react";

import { T9 } from "@src/components/modules/WizardModule/WizardKit";

import type { WizardPage } from "@src/@types/WizardData";

/*
 * The wizard's steps as a vertical stepper, like the create wizards in AWS and the Ops Console:
 * numbered circles joined by a line, done steps ticked, the current one in the brand colour. It
 * sits in the left rail and stays in view while the step scrolls.
 */
type StepState = "done" | "current" | "todo";

const List = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`;
const Item = styled.li<{ last: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: ${props => (props.last ? 0 : 10)}px;
  ${props =>
    props.last
      ? ""
      : css`
          &::after {
            content: "";
            position: absolute;
            left: 11px;
            top: 26px;
            bottom: 0;
            width: 2px;
            border-radius: 1px;
            background: ${T9.slate200};
          }
        `}
`;
const Dot = styled.span<{ state: StepState }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  ${props =>
    props.state === "current"
      ? css`
          background: ${T9.brand600};
          color: white;
        `
      : props.state === "done"
        ? css`
            background: ${T9.brand50};
            color: ${T9.brand700};
            border: 1px solid ${T9.brand300};
          `
        : css`
            background: var(--t9-bg-surface);
            color: ${T9.slate400};
            border: 1px solid ${T9.slate300};
          `}
`;
const Text = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding-top: 3px;
`;
const Name = styled.span<{ state: StepState }>`
  font-size: 13px;
  line-height: 18px;
  font-weight: ${props => (props.state === "current" ? 600 : 500)};
  color: ${props =>
    props.state === "current" ? T9.slate900 : props.state === "done" ? T9.slate600 : T9.slate400};
`;

/** Ops sentence case for the step names ("Source Cloud" -> "Source cloud"; "VMs" stays). */
export const sentenceCase = (label: string) =>
  label
    .split(" ")
    .map((word, i) => (i > 0 && /^[A-Z][a-z]+$/.test(word) ? word.toLowerCase() : word))
    .join(" ");

type Props = {
  selected: { id: string };
  pages: WizardPage[];
};
@observer
class WizardBreadcrumbs extends React.Component<Props> {
  render() {
    const { pages } = this.props;
    const currentIndex = pages.findIndex(p => p.id === this.props.selected.id);
    return (
      <List aria-label="Wizard steps">
        {pages.map((page, i) => {
          const state: StepState = i === currentIndex ? "current" : i < currentIndex ? "done" : "todo";
          return (
            <Item key={page.id} last={i === pages.length - 1} aria-current={state === "current" ? "step" : undefined}>
              <Dot state={state}>{state === "done" ? <Check size={13} strokeWidth={2.5} /> : i + 1}</Dot>
              <Text>
                <Name state={state}>{sentenceCase(page.breadcrumb)}</Name>
              </Text>
            </Item>
          );
        })}
      </List>
    );
  }
}

export default WizardBreadcrumbs;
